import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  Box,
  Chip,
  Checkbox,
} from "@mui/material";
import { stageService, Stage } from "../feature/stageModule";

interface StagesDropdownProps {
  label: string;
  name: string;
  value: string[];
  required?: boolean;
  icon?: React.ReactNode;
  placeholder?: string;
  onChange: (event: any) => void;
  error?: string;
}

const StagesDropdown: React.FC<StagesDropdownProps> = ({
  label,
  name,
  value,
  required,
  icon,
  placeholder,
  onChange,
  error,
}) => {
  const [stages, setStages] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load stages from the service asynchronously
    const loadStages = async () => {
      try {
        setLoading(true);
        const response = await stageService.getAllStages();
        if (response.success) {
          // Deduplicate by name to avoid duplicates if API contains multiple with same name
          const uniqueByName = new Map<string, { value: string; label: string }>();
          (response.data as Stage[]).forEach((stage) => {
            const key = stage.name || stage.id;
            if (!uniqueByName.has(key)) {
              uniqueByName.set(key, { value: stage.id, label: stage.name });
            }
          });
          setStages(Array.from(uniqueByName.values()));
        }
      } catch (error) {
        console.error('Error loading stages:', error);
        setStages([]);
      } finally {
        setLoading(false);
      }
    };

    loadStages();
  }, []);

  const selectedStages = stages.filter(stage => value.includes(stage.value));

  return (
    <FormControl fullWidth required={required}>
      <Autocomplete
        multiple
        options={stages}
        getOptionLabel={(option) => option.label}
        value={selectedStages}
        onChange={(event, newValue) => {
          const newSelectedValues = newValue.map(item => item.value);
          onChange({
            target: {
              value: newSelectedValues,
            },
          } as React.ChangeEvent<{ value: unknown }>);
        }}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            // placeholder={placeholder || "Select stages"}
            required={false}
            error={!!error}
            helperText={error}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              label={option.label}
              {...getTagProps({ index })}
              key={option.value}
            />
          ))
        }
        disableCloseOnSelect
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.label}
          </li>
        )}
      />
    </FormControl>
  );
};

export default StagesDropdown;
